import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  /** Optional custom fallback. When omitted, a default message is rendered. */
  fallback?: ReactNode;
  /**
   * Fallback that receives the caught error (BUG-745: the shells render the
   * chunk-aware error screen). Takes precedence over `fallback`.
   */
  fallbackRender?: (error: unknown) => ReactNode;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: unknown;
}

/**
 * Minimal error boundary.
 *
 * React error boundaries can only be implemented as class components — this is
 * the single sanctioned exception to the "functional components only" rule.
 * It catches render-time errors in its subtree so a localized data-shape issue
 * degrades to a friendly message instead of white-screening the whole app.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep a console trace for diagnostics; no PII is logged here.
    console.error("ErrorBoundary caught an error:", error, info);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallbackRender) {
        return this.props.fallbackRender(this.state.error);
      }
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
